/**
 * REST API Contract (API Gateway)
 *
 * Defines the contract for REST endpoints exposed by the API Gateway (2-api-gateway).
 * These are the public-facing APIs consumed by web and mobile clients.
 *
 * BREAKING CHANGE LOG:
 * - None yet. Initial version.
 */

import { z } from 'zod';

// ============================================================================
// REST ENDPOINT CONTRACTS
// ============================================================================

/**
 * Resource Pagination
 * Used consistently across all list endpoints
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    has_next: boolean;
  };
}

/**
 * Error Response
 * Used consistently across all error responses
 */
export interface ErrorResponse {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// ============================================================================
// AGENT ENDPOINTS CONTRACTS
// ============================================================================

export interface CreateAgentRequest {
  name: string;
  description?: string;
  model_id: string;
  instructions?: string[];
  tools?: {
    name: string;
    description: string;
    schema: string;
  }[];
}

export interface AgentDTO {
  id: string;
  name: string;
  description?: string;
  model_id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'FAILED';
  created_at: string;
  updated_at: string;
}

// ============================================================================
// DOCUMENT ENDPOINTS CONTRACTS
// ============================================================================

export interface IndexDocumentRequest {
  knowledge_base_id: string;
  title: string;
  content: string;
  metadata?: Record<string, string>;
  content_type?: 'TEXT' | 'HTML' | 'MARKDOWN' | 'PDF' | 'JSON';
}

export interface DocumentDTO {
  id: string;
  knowledge_base_id: string;
  title: string;
  content: string;
  status: 'INDEXED' | 'INDEXING' | 'FAILED';
  created_at: string;
  updated_at: string;
}

// ============================================================================
// USER ENDPOINTS CONTRACTS
// ============================================================================

export interface UserDTO {
  id: string;
  email: string;
  display_name: string;
  role: 'ADMIN' | 'USER' | 'MODERATOR';
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  display_name: string;
  role?: 'USER' | 'ADMIN' | 'MODERATOR';
}

// ============================================================================
// AUTH ENDPOINTS CONTRACTS
// ============================================================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: 'Bearer';
}

export interface RegisterRequest {
  email: string;
  password: string;
  display_name: string;
}

// ============================================================================
// VALIDATION SCHEMAS
// ============================================================================

export const createAgentRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1024).optional(),
  model_id: z.string().min(1),
  instructions: z.array(z.string()).optional(),
  tools: z
    .array(
      z.object({
        name: z.string().min(1),
        description: z.string(),
        schema: z.string(),
      }),
    )
    .optional(),
});

export const indexDocumentRequestSchema = z.object({
  knowledge_base_id: z.string().min(1),
  title: z.string().min(1).max(255),
  content: z.string().min(1),
  metadata: z.record(z.string(), z.string()).optional(),
  content_type: z.enum(['TEXT', 'HTML', 'MARKDOWN', 'PDF', 'JSON']).optional(),
});

export const loginRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

export const registerRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  display_name: z.string().min(1).max(255),
});

// ============================================================================
// HTTP STATUS CODES CONTRACT
// ============================================================================

/**
 * Standard HTTP status code usage across all endpoints
 * CRITICAL: Clients depend on these status codes
 */
export const HTTP_STATUS_CONTRACT = {
  SUCCESS: {
    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NO_CONTENT: 204,
  },
  CLIENT_ERROR: {
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    UNPROCESSABLE_ENTITY: 422,
    TOO_MANY_REQUESTS: 429,
  },
  SERVER_ERROR: {
    INTERNAL_SERVER_ERROR: 500,
    NOT_IMPLEMENTED: 501,
    SERVICE_UNAVAILABLE: 503,
  },
} as const;

// ============================================================================
// RESPONSE HEADER CONTRACTS
// ============================================================================

/**
 * Standard response headers across all endpoints
 * CRITICAL: Clients may depend on these headers
 */
export const RESPONSE_HEADERS_CONTRACT = {
  'Content-Type': 'application/json',
  'X-Request-ID': 'string',
  'X-RateLimit-Limit': 'number',
  'X-RateLimit-Remaining': 'number',
  'X-RateLimit-Reset': 'number',
} as const;

// ============================================================================
// SERVICE CONTRACT INTERFACE
// ============================================================================

export interface IRESTAPIContract {
  /**
   * POST /api/v1/agents
   * CRITICAL: Response schema and status code 201
   */
  createAgent(request: CreateAgentRequest): Promise<AgentDTO>;

  /**
   * GET /api/v1/agents/:id
   * CRITICAL: Response schema stability
   */
  getAgent(agentId: string): Promise<AgentDTO>;

  /**
   * GET /api/v1/agents
   * CRITICAL: Pagination contract must not change
   */
  listAgents(query?: { page?: number; limit?: number }): Promise<PaginatedResponse<AgentDTO>>;

  /**
   * POST /api/v1/auth/login
   * CRITICAL: Token format and expiry
   */
  login(request: LoginRequest): Promise<AuthTokenResponse>;

  /**
   * POST /api/v1/auth/register
   * CRITICAL: Response must match login token format
   */
  register(request: RegisterRequest): Promise<AuthTokenResponse>;
}

// ============================================================================
// SCHEMA VERSIONS
// ============================================================================

export const REST_API_CONTRACT_VERSION = '1.0.0';

export const RESTAPIContractMetadata = {
  version: REST_API_CONTRACT_VERSION,
  apiVersion: 'v1',
  baseUrl: '/api/v1',
  lastUpdated: '2025-10-24',
  breakers: [] as string[],
  notes: 'Initial REST API contract definition',
} as const;
